import React, { useMemo, useState } from 'react';
import { RoomOpenGraph } from '../../definitions/schemas';
import FullScreenDialog from '../Dialog/FullScreenDialog';
import UrlViewer, { UrlViewerProps } from './UrlViewer';
import RenderIframe from './RenderIframe';
import { StorageProviderMTPickers } from '../../enums/Integrations/StorageProviders';
import FederatedAssetHubViewer from './FederatedAssetHubViewer';
import { OrganizationFlagEnum } from '../../lib/features/FeatureFlag';
import useFeatureFlag from '../../lib/hook/useFeatureFlag';
import { THEATER_FRAME_HEIGHT } from './helper';
import useTheaterFrameHeight from '../../lib/hook/useTheaterFrameHeight';

type PickedUrlViewerProps = Omit<UrlViewerProps, 'onClick'>;

export interface FullScreenUrlViewerProps extends PickedUrlViewerProps {
  forceFullScreen?: boolean;
  isExpired?: boolean;
  onFullScreenChange?: (fullScreen: boolean) => void;
  onUrlClicked?: () => void;
  openGraph?: RoomOpenGraph;
  track?: boolean;
  onWindowMessageReceived: (event: MessageEvent) => void;
  content?: any;
}

const FullScreenUrlViewer: React.FC<FullScreenUrlViewerProps> = (
  props: FullScreenUrlViewerProps
): JSX.Element => {
  const {
    forceFullScreen = false,
    iframe,
    name,
    onFullScreenChange,
    onUrlClicked,
    openGraph,
    url,
    onWindowMessageReceived,
    ...urlViewerProps
  } = props;

  const isFederatedAssetEnabled = useFeatureFlag(
    OrganizationFlagEnum.FEDERATED_ASSET_ENABLED
  );

  const [open, setOpen] = useState(false);

  const isAssetHubContentProvider =
    props?.content?.mtData?.provider === StorageProviderMTPickers.AssetHub;

  // Theater mode: size the frame to the measured remaining space so its
  // bottom zoom toolbar stays visible without a second scrollbar or gap
  const { ref: theaterRef, height: theaterHeight } = useTheaterFrameHeight(
    !!(iframe && forceFullScreen)
  );

  const urlIframe = useMemo(() => {
    if (openGraph?.videoSecureUrl) {
      return openGraph.videoSecureUrl;
    }

    // URLs without protocol will load relative to our domain
    // so we append the protocol to force the iframe to externalize the url
    if (url.startsWith('http')) {
      return url;
    }

    return `https://${url}`;
  }, [openGraph, url]);

  const toggleOpen = () => {
    const isOpen = !open;
    setOpen(isOpen);

    if (onFullScreenChange) {
      onFullScreenChange(isOpen);
    }
  };

  const handleClick = () => {
    if (iframe) {
      toggleOpen();
      return;
    }

    if (onUrlClicked) {
      onUrlClicked();
    }
  };

  if (iframe && forceFullScreen) {
    if (isAssetHubContentProvider && isFederatedAssetEnabled) {
      return (
        <FederatedAssetHubViewer
          url={url}
          name={name}
          onWindowMessageReceived={onWindowMessageReceived}
          forceFullScreen
          content={props.content || {}}
        />
      );
    }

    return (
      <RenderIframe
        name={name}
        onWindowMessageReceived={onWindowMessageReceived}
        urlIframe={urlIframe}
        height={theaterHeight ?? THEATER_FRAME_HEIGHT}
        wrapperRef={theaterRef}
      />
    );
  }

  return (
    <>
      <UrlViewer
        {...urlViewerProps}
        iframe={iframe}
        name={name}
        onClick={handleClick}
        url={url}
      />
      <FullScreenDialog
        onClose={toggleOpen}
        open={open}
        title={name}
        // The iframe scrolls its own content — the dialog layout must not
        // scroll on top of it nor reserve a scrollbar gutter
        LayoutContentProps={{ disableOverflow: true, disableScroll: true }}
      >
        {iframe &&
          (isAssetHubContentProvider && isFederatedAssetEnabled ? (
            <FederatedAssetHubViewer
              url={url}
              name={name}
              onWindowMessageReceived={onWindowMessageReceived}
              height="calc(100vh - 120px)"
              content={props.content || {}}
            />
          ) : (
            <RenderIframe
              name={name}
              onWindowMessageReceived={onWindowMessageReceived}
              urlIframe={urlIframe}
            />
          ))}
      </FullScreenDialog>
    </>
  );
};

export default FullScreenUrlViewer;
